/**
 * NuevaBonificacionPage — Wizard de solicitud de reembolso.
 *
 * 100% conectado al backend real (sin mocks):
 *  - usePetsList: mascotas del usuario
 *  - useSubscriptions: subs activas (necesitamos subscriptionId)
 *  - usePlanDetail: coverages del plan asociado a la mascota
 *  - useCreateClaim: crea el claim en /api/v1/claims
 *
 * 6 pasos: Mascota → Veterinario → Diagnóstico → Items (con coverages reales)
 *          → Documentos → Resumen.
 *
 * Responsive: cards apilan en mobile, sticky stepper, touch targets ≥ 44px.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PageHeader } from '@app/components/common/PageHeader';
import { EmptyState } from '@app/components/common/EmptyState';
import { usePetsList } from '@modules/pets/presentation/hooks/usePets';
import { useSubscriptions } from '@modules/billing/presentation/hooks/useBilling';
import { usePlan, usePlanDetail } from '@modules/plans/presentation/hooks/usePlans';
import { useCreateClaim } from '@modules/claims/presentation/hooks/useClaims';
import { claimsApi } from '@modules/claims/infrastructure/repositories/http/ClaimsHttpRepository';
import type { CreateClaimRequest } from '@modules/claims/application/dto/ClaimDTO';
import { getErrorMessage } from '@shared/infrastructure/http/api.error';
import { formatCLP } from '@shared/utils/formatters';
import { cn } from '@shared/utils/cn';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  PawPrint,
  Stethoscope,
  FileText,
  Plus,
  Trash2,
  Upload,
  AlertCircle,
  Receipt,
  CheckCircle2,
  Lock,
  Clock,
  Info,
  ShieldOff,
} from 'lucide-react';

type StepKey = 'pet' | 'vet' | 'diagnosis' | 'items' | 'docs' | 'summary';
const STEPS: { key: StepKey; label: string; icon: typeof PawPrint }[] = [
  { key: 'pet', label: 'Mascota', icon: PawPrint },
  { key: 'vet', label: 'Veterinario', icon: Stethoscope },
  { key: 'diagnosis', label: 'Diagnóstico', icon: FileText },
  { key: 'items', label: 'Prestaciones', icon: Receipt },
  { key: 'docs', label: 'Documentos', icon: Upload },
  { key: 'summary', label: 'Resumen', icon: CheckCircle2 },
];

interface ItemForm {
  coverageTypeId: string;
  description: string;
  amountClp: number;
}

const MIN_DAYS_ACTIVE = 30;

function daysBetween(iso: string): number {
  const start = new Date(iso);
  if (Number.isNaN(start.getTime())) return 0;
  const diff = Date.now() - start.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

function addDays(iso: string, days: number): Date {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d;
}

function formatShortDate(d: Date): string {
  try {
    return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

type EligibilityStatus =
  | { kind: 'eligible'; days: number }
  | { kind: 'waiting'; daysRemaining: number; availableAt: Date; days: number }
  | { kind: 'no_plan' };

interface PetEligibility {
  status: EligibilityStatus;
  subscriptionId?: string;
}

function evaluatePet(
  petId: string,
  subscriptions: { id: string; petId: string; status: string; createdAt: string }[],
): PetEligibility {
  const sub = subscriptions.find(
    (s) => s.petId === petId && ['ACTIVE', 'PAST_DUE'].includes((s.status ?? '').toUpperCase()),
  );
  if (!sub) return { status: { kind: 'no_plan' } };
  const days = daysBetween(sub.createdAt);
  if (days >= MIN_DAYS_ACTIVE) {
    return { status: { kind: 'eligible', days }, subscriptionId: sub.id };
  }
  return {
    status: {
      kind: 'waiting',
      daysRemaining: MIN_DAYS_ACTIVE - days,
      availableAt: addDays(sub.createdAt, MIN_DAYS_ACTIVE),
      days,
    },
    subscriptionId: sub.id,
  };
}

export function NuevaBonificacionPage() {
  const navigate = useNavigate();
  const createClaim = useCreateClaim();
  const { data: pets = [], isLoading: loadingPets } = usePetsList();
  const { data: subscriptions = [] } = useSubscriptions();

  const [stepIndex, setStepIndex] = useState(0);
  const currentStep = STEPS[stepIndex];

  // Estado del wizard
  const [petId, setPetId] = useState<string>('');
  const [vetName, setVetName] = useState('');
  const [vetClinic, setVetClinic] = useState('');
  const [attentionDate, setAttentionDate] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [treatmentDescription, setTreatmentDescription] = useState('');
  const [items, setItems] = useState<ItemForm[]>([
    { coverageTypeId: '', description: '', amountClp: 0 },
  ]);
  const [requestFormFile, setRequestFormFile] = useState<File | null>(null);
  const [invoiceFiles, setInvoiceFiles] = useState<File[]>([]);
  const [comment, setComment] = useState('');

  // Resolver subscription + plan de la mascota seleccionada
  const selectedPet = pets.find((p) => p.id === petId);
  const subscriptionForPet = useMemo(
    () =>
      subscriptions.find(
        (s) => s.petId === petId && ['ACTIVE', 'PAST_DUE'].includes((s.status ?? '').toUpperCase()),
      ),
    [subscriptions, petId],
  );
  const { data: planDetail, isLoading: loadingPlan } = usePlanDetail(
    subscriptionForPet?.planId,
  );
  const { data: planWithCoverages } = usePlan(subscriptionForPet?.planId);
  const availableCoverages = planDetail?.coverages ?? [];

  // Días activos de la subscripción
  const daysActive = subscriptionForPet
    ? daysBetween(subscriptionForPet.createdAt)
    : 0;
  const eligibilityIssue: string | null = (() => {
    if (!selectedPet) return null;
    if (!subscriptionForPet)
      return `${selectedPet.name} no tiene una suscripción activa. Contratá un plan primero.`;
    if (daysActive < MIN_DAYS_ACTIVE)
      return `${selectedPet.name} necesita al menos ${MIN_DAYS_ACTIVE} días de suscripción activa. Lleva ${daysActive} día${daysActive === 1 ? '' : 's'}.`;
    return null;
  })();

  // Total
  const totalClp = items.reduce((sum, it) => sum + (it.amountClp || 0), 0);

  // Reset items cuando cambia el plan (para que el selector tenga las coverages correctas)
  useEffect(() => {
    setItems((prev) => {
      let changed = false;
      const next = prev.map((it) => {
        const stillValid = availableCoverages.some(
          (cv) => cv.id === it.coverageTypeId,
        );
        if (stillValid || !it.coverageTypeId) return it;
        changed = true;
        return { ...it, coverageTypeId: '' };
      });
      return changed ? next : prev;
    });
  }, [availableCoverages]);

  // Validación por paso
  const canAdvance = (): { ok: boolean; reason?: string } => {
    if (currentStep.key === 'pet') {
      if (!petId) return { ok: false, reason: 'Seleccioná una mascota' };
      if (eligibilityIssue) return { ok: false, reason: eligibilityIssue };
      return { ok: true };
    }
    if (currentStep.key === 'vet') {
      if (vetName.trim().length < 2) return { ok: false, reason: 'Nombre del veterinario es requerido' };
      if (!attentionDate) return { ok: false, reason: 'Fecha de atención es requerida' };
      const date = new Date(attentionDate);
      if (Number.isNaN(date.getTime())) return { ok: false, reason: 'Fecha inválida' };
      if (date > new Date()) return { ok: false, reason: 'La fecha no puede ser futura' };
      return { ok: true };
    }
    if (currentStep.key === 'diagnosis') {
      if (diagnosis.trim().length < 10)
        return { ok: false, reason: 'El diagnóstico debe tener al menos 10 caracteres' };
      return { ok: true };
    }
    if (currentStep.key === 'items') {
      if (items.length === 0) return { ok: false, reason: 'Agregá al menos una prestación' };
      for (const [i, it] of items.entries()) {
        if (!it.coverageTypeId)
          return { ok: false, reason: `Prestación #${i + 1}: seleccioná el tipo de cobertura` };
        if (!planWithCoverages?.coverages?.find((cv) => cv.id === it.coverageTypeId)?.coverageTypeId)
          return { ok: false, reason: 'No se pudo verificar la cobertura del plan. Intenta nuevamente.' };
        if (!it.description.trim())
          return { ok: false, reason: `Prestación #${i + 1}: agregá una descripción` };
        if (!it.amountClp || it.amountClp <= 0)
          return { ok: false, reason: `Prestación #${i + 1}: ingresá un monto mayor a 0` };
      }
      return { ok: true };
    }
    if (currentStep.key === 'docs') {
      if (!requestFormFile) return { ok: false, reason: 'Subí el formulario de solicitud' };
      if (!requestFormFile.name.toLowerCase().endsWith('.pdf')) return { ok: false, reason: 'El formulario debe ser un archivo PDF' };
      if (requestFormFile.size > 20 * 1024 * 1024) return { ok: false, reason: 'El formulario no puede superar 20 MB' };
      if (invoiceFiles.length === 0)
        return { ok: false, reason: 'Subí al menos una boleta o factura' };
      if (invoiceFiles.some((file) => file.size > 5 * 1024 * 1024))
        return { ok: false, reason: 'Cada boleta o factura debe pesar como máximo 5 MB' };
      if (invoiceFiles.some((file) => !/\.(pdf|jpe?g|png)$/i.test(file.name)))
        return { ok: false, reason: 'Las boletas deben ser PDF, JPG o PNG' };
      return { ok: true };
    }
    return { ok: true };
  };

  const validation = canAdvance();

  const handleNext = () => {
    if (!validation.ok) {
      if (validation.reason) toast.error(validation.reason);
      return;
    }
    setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  const [uploadProgress, setUploadProgress] = useState<{
    total: number;
    done: number;
  } | null>(null);

  const handleSubmit = async () => {
    if (!subscriptionForPet) return;
    try {
      const body: CreateClaimRequest = {
        petId,
        subscriptionId: subscriptionForPet.id,
        vetName: vetName.trim(),
        vetClinic: vetClinic.trim() || undefined,
        attentionDate,
        diagnosis: diagnosis.trim(),
        treatmentDescription: treatmentDescription.trim() || undefined,
        comment: comment.trim() || undefined,
        items: items.map((it) => ({
          coverageTypeId: planWithCoverages!.coverages!.find((cv) => cv.id === it.coverageTypeId)!.coverageTypeId,
          description: it.description.trim(),
          amountClp: it.amountClp,
        })),
      };

      // 1) Crear el claim
      const result = await createClaim.mutateAsync(body);
      const claimId = result.claimId;

      // 2) Subir archivos asociados al claim (no abortar el flow si alguno falla;
      //    el claim ya está creado y el usuario puede re-subir desde el detalle).
      const totalUploads = (requestFormFile ? 1 : 0) + invoiceFiles.length;
      if (totalUploads > 0) {
        setUploadProgress({ total: totalUploads, done: 0 });
        const failures: string[] = [];

        if (requestFormFile) {
          try {
            await claimsApi.uploadRequestForm(claimId, requestFormFile);
          } catch (err) {
            failures.push(`Formulario: ${getErrorMessage(err)}`);
          }
          setUploadProgress((p) => (p ? { ...p, done: p.done + 1 } : p));
        }

        for (const invoice of invoiceFiles) {
          try {
            await claimsApi.uploadInvoice(claimId, invoice);
          } catch (err) {
            failures.push(`${invoice.name}: ${getErrorMessage(err)}`);
          }
          setUploadProgress((p) => (p ? { ...p, done: p.done + 1 } : p));
        }

        setUploadProgress(null);

        if (failures.length > 0) {
          toast.warning(
            `Solicitud creada (N° ${result.claimNumber}), pero ${failures.length} archivo${
              failures.length === 1 ? '' : 's'
            } no se pudo subir. Podés reintentarlo desde el detalle.`,
          );
          navigate(`/bonificaciones/${claimId}`);
          return;
        }
      }

      toast.success(`Solicitud N° ${result.claimNumber} creada exitosamente`);
      navigate('/bonificaciones');
    } catch (err) {
      setUploadProgress(null);
      toast.error(getErrorMessage(err));
    }
  };

  const addItem = () =>
    setItems((prev) => [...prev, { coverageTypeId: '', description: '', amountClp: 0 }]);
  const removeItem = (idx: number) =>
    setItems((prev) => prev.filter((_, i) => i !== idx));
  const updateItem = (idx: number, patch: Partial<ItemForm>) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  if (loadingPets) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-80 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        className="-ml-2 rounded-full text-muted-foreground hover:text-foreground"
        onClick={() => navigate('/bonificaciones')}
      >
        <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" />
        Volver
      </Button>

      <PageHeader
        title="Nueva solicitud de reembolso"
        subtitle={`Paso ${stepIndex + 1} de ${STEPS.length}: ${currentStep.label}`}
      />

      {/* Stepper */}
      <div className="overflow-x-auto">
        <ol className="flex min-w-max gap-2 sm:gap-3">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i < stepIndex;
            const active = i === stepIndex;
            return (
              <li key={s.key} className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => i < stepIndex && setStepIndex(i)}
                  disabled={i > stepIndex}
                  className={cn(
                    'flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-medium transition-colors',
                    'min-h-[44px]',
                    active && 'border-primary bg-primary text-primary-foreground',
                    done && !active && 'border-emerald-500/40 bg-emerald-50 text-emerald-700',
                    !active && !done && 'border-border bg-card text-muted-foreground',
                    i > stepIndex && 'cursor-not-allowed opacity-50',
                  )}
                >
                  {done ? (
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  ) : (
                    <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  <span className="hidden sm:inline">{s.label}</span>
                  <span className="sm:hidden">{i + 1}</span>
                </button>
                {i < STEPS.length - 1 && (
                  <span className="h-px w-4 shrink-0 bg-border sm:w-8" aria-hidden="true" />
                )}
              </li>
            );
          })}
        </ol>
      </div>

      {/* Paso: Mascota */}
      {currentStep.key === 'pet' && (
        <section className="space-y-4 rounded-2xl border border-border bg-card p-5 shadow-xs sm:p-6">
          <div>
            <h2 className="text-lg font-bold tracking-tight">Seleccioná tu mascota</h2>
            <p className="text-sm text-muted-foreground">
              Elegí para qué mascota es este reembolso.
            </p>
          </div>

          {/* Info banner explicando la regla de carencia */}
          <div className="flex items-start gap-2.5 rounded-xl border border-sidebar-primary/20 bg-sidebar-accent/40 p-3 text-sm">
            <Info
              className="mt-0.5 h-4 w-4 shrink-0 text-sidebar-primary"
              aria-hidden="true"
            />
            <div className="space-y-1">
              <p className="font-medium text-foreground">
                Período de carencia: {MIN_DAYS_ACTIVE} días
              </p>
              <p className="text-xs text-muted-foreground">
                Cada mascota debe tener su plan activo por al menos {MIN_DAYS_ACTIVE} días corridos
                antes de poder solicitar el primer reembolso. Es una protección estándar de los
                seguros de asistencia.
              </p>
            </div>
          </div>

          {pets.length === 0 ? (
            <EmptyState
              icon={PawPrint}
              title="Aún no tenés mascotas registradas"
              description="Agregá tu primera mascota desde el inicio para poder solicitar reembolsos."
              action={
                <Button asChild className="rounded-full">
                  <a href="/contratacion">Agregar mascota</a>
                </Button>
              }
            />
          ) : (
            <>
              <ul
                className="grid grid-cols-1 gap-3 sm:grid-cols-2"
                role="radiogroup"
                aria-label="Seleccionar mascota para el reembolso"
              >
                {pets.map((p) => {
                  const evalResult = evaluatePet(p.id, subscriptions);
                  const isSelected = petId === p.id;
                  const isEligible = evalResult.status.kind === 'eligible';

                  return (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => isEligible && setPetId(p.id)}
                        disabled={!isEligible}
                        role="radio"
                        aria-checked={isSelected}
                        aria-disabled={!isEligible}
                        aria-label={
                          isEligible
                            ? `Seleccionar a ${p.name}`
                            : evalResult.status.kind === 'waiting'
                            ? `${p.name} estará disponible en ${evalResult.status.daysRemaining} días`
                            : `${p.name} no tiene plan activo`
                        }
                        className={cn(
                          'flex w-full items-start gap-3 rounded-2xl border p-4 text-left transition-all',
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                          isSelected &&
                            'border-primary bg-primary/5 shadow-sm ring-1 ring-primary/30',
                          !isSelected &&
                            isEligible &&
                            'cursor-pointer border-border bg-card hover:border-foreground/15 hover:shadow-md',
                          !isEligible &&
                            'cursor-not-allowed border-dashed border-border bg-muted/30',
                        )}
                      >
                        {/* Avatar */}
                        <span
                          className={cn(
                            'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
                            isEligible
                              ? 'bg-accent text-accent-foreground'
                              : 'bg-muted text-muted-foreground',
                          )}
                        >
                          {p.photoUrl ? (
                            <img
                              src={p.photoUrl}
                              alt=""
                              className="h-full w-full rounded-xl object-cover"
                            />
                          ) : (
                            <PawPrint
                              className="h-5 w-5"
                              strokeWidth={1.75}
                              aria-hidden="true"
                            />
                          )}
                        </span>

                        {/* Info */}
                        <div className="min-w-0 flex-1 space-y-1.5">
                          <div className="flex flex-wrap items-center gap-2">
                            <p
                              className={cn(
                                'truncate text-sm font-semibold',
                                isEligible ? 'text-foreground' : 'text-muted-foreground',
                              )}
                            >
                              {p.name}
                            </p>
                            {/* Badge de estado */}
                            <EligibilityBadge status={evalResult.status} />
                          </div>
                          <p className="truncate text-xs text-muted-foreground">
                            {p.breed ?? p.species}
                          </p>
                          {/* Línea explicativa según estado */}
                          <EligibilityHelp status={evalResult.status} petName={p.name} />
                        </div>

                        {/* Indicador derecho */}
                        <span className="flex h-5 shrink-0 items-center">
                          {isSelected ? (
                            <CheckCircle2
                              className="h-5 w-5 text-primary"
                              aria-hidden="true"
                            />
                          ) : isEligible ? null : (
                            <Lock
                              className="h-4 w-4 text-muted-foreground"
                              aria-hidden="true"
                            />
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              {/* Si todas están bloqueadas, mensaje contundente */}
              {pets.length > 0 &&
                !pets.some((p) => evaluatePet(p.id, subscriptions).status.kind === 'eligible') && (
                  <div className="flex items-start gap-2.5 rounded-xl border border-chart-4/40 bg-chart-4/15 p-4">
                    <AlertCircle
                      className="mt-0.5 h-4 w-4 shrink-0 text-chart-4"
                      aria-hidden="true"
                    />
                    <div className="space-y-1 text-sm">
                      <p className="font-semibold text-foreground">
                        Aún ninguna mascota puede pedir reembolso
                      </p>
                      <p className="text-xs text-foreground/80">
                        Esto puede ser porque alguna no tiene un plan activo o porque el plan se
                        contrató hace menos de {MIN_DAYS_ACTIVE} días. Revisá los estados arriba —
                        cuando se complete la carencia podrás solicitar.
                      </p>
                    </div>
                  </div>
                )}
            </>
          )}
        </section>
      )}

      {/* Paso: Veterinario */}
      {currentStep.key === 'vet' && (
        <section className="space-y-4 rounded-2xl border border-border bg-card p-5 shadow-xs sm:p-6">
          <div>
            <h2 className="text-lg font-bold tracking-tight">Datos del veterinario</h2>
            <p className="text-sm text-muted-foreground">¿Quién atendió a {selectedPet?.name}?</p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="vet-name">
                Nombre del veterinario <span className="text-destructive">*</span>
              </Label>
              <Input
                id="vet-name"
                value={vetName}
                onChange={(e) => setVetName(e.target.value)}
                placeholder="Dra. Camila Pérez"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="vet-clinic">Clínica veterinaria</Label>
              <Input
                id="vet-clinic"
                value={vetClinic}
                onChange={(e) => setVetClinic(e.target.value)}
                placeholder="Centro Veterinario Dr. Cafati"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="attention-date">
                Fecha de atención <span className="text-destructive">*</span>
              </Label>
              <Input
                id="attention-date"
                type="date"
                value={attentionDate}
                onChange={(e) => setAttentionDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-muted-foreground">No puede ser futura.</p>
            </div>
          </div>
        </section>
      )}

      {/* Paso: Diagnóstico */}
      {currentStep.key === 'diagnosis' && (
        <section className="space-y-4 rounded-2xl border border-border bg-card p-5 shadow-xs sm:p-6">
          <div>
            <h2 className="text-lg font-bold tracking-tight">Información médica</h2>
            <p className="text-sm text-muted-foreground">
              Detallá el diagnóstico y tratamiento aplicado.
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="diagnosis">
                Diagnóstico <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="diagnosis"
                value={diagnosis}
                onChange={(e) => setDiagnosis(e.target.value)}
                placeholder="Describe el diagnóstico médico…"
                rows={4}
                className="min-h-[100px]"
              />
              <p className="text-xs text-muted-foreground">Mínimo 10 caracteres.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="treatment">Tratamiento</Label>
              <Textarea
                id="treatment"
                value={treatmentDescription}
                onChange={(e) => setTreatmentDescription(e.target.value)}
                placeholder="Procedimientos, medicación, recomendaciones…"
                rows={4}
                className="min-h-[100px]"
              />
            </div>
          </div>
        </section>
      )}

      {/* Paso: Items */}
      {currentStep.key === 'items' && (
        <section className="space-y-4 rounded-2xl border border-border bg-card p-5 shadow-xs sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold tracking-tight">Prestaciones</h2>
              <p className="text-sm text-muted-foreground">
                Detallá cada servicio que querés reembolsar.
              </p>
            </div>
            {loadingPlan && <Skeleton className="h-5 w-32" />}
          </div>

          {availableCoverages.length === 0 && !loadingPlan && (
            <div className="flex items-start gap-2 rounded-xl border border-chart-4/40 bg-chart-4/15 p-3 text-sm">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-chart-4" aria-hidden="true" />
              <p className="text-foreground/80">
                Tu plan no tiene coberturas configuradas. Contactá a soporte para revisarlo.
              </p>
            </div>
          )}

          <div className="space-y-3">
            {items.map((it, idx) => (
              <div
                key={idx}
                className="space-y-3 rounded-xl border border-border bg-muted/30 p-4"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-foreground">Prestación #{idx + 1}</p>
                  {items.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full text-destructive hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => removeItem(idx)}
                      aria-label={`Eliminar prestación ${idx + 1}`}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor={`item-coverage-${idx}`}>Tipo de cobertura</Label>
                    <Select
                      value={it.coverageTypeId}
                      onValueChange={(v) => updateItem(idx, { coverageTypeId: v })}
                    >
                      <SelectTrigger id={`item-coverage-${idx}`}>
                        <SelectValue placeholder="Elegí una cobertura del plan" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableCoverages.map((cv) => (
                          <SelectItem key={cv.id} value={cv.id}>
                            {cv.name}
                            {cv.coverage_percentage
                              ? ` · ${Math.round(cv.coverage_percentage * 100)}%`
                              : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`item-desc-${idx}`}>Descripción</Label>
                    <Input
                      id={`item-desc-${idx}`}
                      value={it.description}
                      onChange={(e) => updateItem(idx, { description: e.target.value })}
                      placeholder="Consulta, vacuna, examen…"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`item-amount-${idx}`}>Monto (CLP)</Label>
                    <Input
                      id={`item-amount-${idx}`}
                      type="number"
                      inputMode="numeric"
                      min={0}
                      value={it.amountClp || ''}
                      onChange={(e) =>
                        updateItem(idx, { amountClp: Number(e.target.value) || 0 })
                      }
                      placeholder="30000"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full rounded-full"
            onClick={addItem}
          >
            <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
            Agregar otra prestación
          </Button>

          <div className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3">
            <span className="text-sm font-medium text-foreground">Total solicitado</span>
            <span className="text-lg font-bold tabular-nums text-primary">{formatCLP(totalClp)}</span>
          </div>
        </section>
      )}

      {/* Paso: Documentos */}
      {currentStep.key === 'docs' && (
        <section className="space-y-4 rounded-2xl border border-border bg-card p-5 shadow-xs sm:p-6">
          <div>
            <h2 className="text-lg font-bold tracking-tight">Documentos</h2>
            <p className="text-sm text-muted-foreground">
              Subí el formulario firmado y las boletas o facturas.
            </p>
          </div>

          {/* Formulario de solicitud */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Formulario de solicitud <span className="text-destructive">*</span>
            </Label>
            <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-muted/30 px-4 py-6 text-center transition-colors hover:border-foreground/20 hover:bg-muted/50">
              <Upload className="h-7 w-7 text-muted-foreground" strokeWidth={1.5} aria-hidden="true" />
              {requestFormFile ? (
                <p className="text-sm font-medium text-foreground">{requestFormFile.name}</p>
              ) : (
                <>
                  <p className="text-sm font-medium text-foreground">
                    Toca para subir el formulario
                  </p>
                  <p className="text-xs text-muted-foreground">PDF, máx. 20 MB</p>
                </>
              )}
              <input
                type="file"
                accept=".pdf"
                className="sr-only"
                onChange={(e) => setRequestFormFile(e.target.files?.[0] ?? null)}
              />
            </label>
          </div>

          {/* Boletas/Facturas */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Boletas / Facturas <span className="text-destructive">*</span>
            </Label>
            <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-muted/30 px-4 py-6 text-center transition-colors hover:border-foreground/20 hover:bg-muted/50">
              <Upload className="h-7 w-7 text-muted-foreground" strokeWidth={1.5} aria-hidden="true" />
              <p className="text-sm font-medium text-foreground">
                {invoiceFiles.length > 0
                  ? `${invoiceFiles.length} archivo${invoiceFiles.length === 1 ? '' : 's'} seleccionado${invoiceFiles.length === 1 ? '' : 's'}`
                  : 'Toca para subir boletas'}
              </p>
              <p className="text-xs text-muted-foreground">PDF o imágenes (JPG/PNG), máx. 5MB c/u</p>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                multiple
                className="sr-only"
                onChange={(e) => setInvoiceFiles(Array.from(e.target.files ?? []))}
              />
            </label>
            {invoiceFiles.length > 0 && (
              <ul className="space-y-1 text-xs text-muted-foreground">
                {invoiceFiles.map((f, i) => (
                  <li key={i} className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3 w-3 text-emerald-500" aria-hidden="true" />
                    {f.name}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="comment">Comentarios adicionales</Label>
            <Textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Información adicional para el revisor…"
              rows={3}
            />
          </div>
          {!validation.ok && validation.reason && (requestFormFile || invoiceFiles.length > 0) && (
            <p role="alert" className="text-sm text-destructive">{validation.reason}</p>
          )}
        </section>
      )}

      {/* Paso: Resumen */}
      {currentStep.key === 'summary' && (
        <section className="space-y-4 rounded-2xl border border-border bg-card p-5 shadow-xs sm:p-6">
          <div>
            <h2 className="text-lg font-bold tracking-tight">Revisá y confirmá</h2>
            <p className="text-sm text-muted-foreground">
              Verificá los datos antes de enviar la solicitud.
            </p>
          </div>

          <dl className="space-y-3 divide-y divide-border">
            <div className="flex flex-wrap justify-between gap-2 pt-3 first:pt-0">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Mascota</dt>
              <dd className="text-sm font-medium text-foreground">
                {selectedPet?.name} · {selectedPet?.breed ?? selectedPet?.species}
              </dd>
            </div>
            <div className="flex flex-wrap justify-between gap-2 pt-3">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Plan</dt>
              <dd className="text-sm font-medium text-foreground">
                {subscriptionForPet?.planName ?? '—'}
              </dd>
            </div>
            <div className="flex flex-wrap justify-between gap-2 pt-3">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Veterinario</dt>
              <dd className="text-sm font-medium text-foreground">
                {vetName}
                {vetClinic ? ` · ${vetClinic}` : ''}
              </dd>
            </div>
            <div className="flex flex-wrap justify-between gap-2 pt-3">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Fecha</dt>
              <dd className="text-sm font-medium text-foreground">{attentionDate}</dd>
            </div>
            <div className="flex flex-wrap justify-between gap-2 pt-3">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                Prestaciones
              </dt>
              <dd className="text-sm font-medium text-foreground">
                {items.length} item{items.length === 1 ? '' : 's'}
              </dd>
            </div>
            <div className="flex flex-wrap justify-between gap-2 pt-3">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Documentos</dt>
              <dd className="text-sm font-medium text-foreground">
                {requestFormFile ? '1 formulario' : '—'} ·{' '}
                {invoiceFiles.length} boleta{invoiceFiles.length === 1 ? '' : 's'}
              </dd>
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-2 pt-3">
              <dt className="text-sm font-semibold text-foreground">Total solicitado</dt>
              <dd className="text-xl font-bold tabular-nums text-primary">{formatCLP(totalClp)}</dd>
            </div>
          </dl>

          <div className="flex items-start gap-2 rounded-xl border border-chart-4/40 bg-chart-4/15 p-3 text-sm">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-chart-4" aria-hidden="true" />
            <p className="text-foreground/80">
              Una vez enviada, el equipo revisará tu solicitud y te avisará por notificación.
              No vas a poder editarla después.
            </p>
          </div>
        </section>
      )}

      {/* Navegación */}
      <div className="sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card p-3 shadow-md sm:p-4">
        <Button
          variant="outline"
          className="rounded-full"
          onClick={handleBack}
          disabled={stepIndex === 0 || createClaim.isPending}
        >
          <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" />
          Atrás
        </Button>
        {currentStep.key === 'summary' ? (
          <Button
            className="rounded-full"
            onClick={handleSubmit}
            disabled={createClaim.isPending || !!uploadProgress || !validation.ok}
          >
            {createClaim.isPending
              ? 'Creando solicitud…'
              : uploadProgress
              ? `Subiendo archivos (${uploadProgress.done}/${uploadProgress.total})…`
              : 'Enviar solicitud'}
            <Check className="ml-1 h-4 w-4" aria-hidden="true" />
          </Button>
        ) : (
          <Button
            className="rounded-full"
            onClick={handleNext}
            disabled={!validation.ok}
          >
            Siguiente
            <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
          </Button>
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Sub-componentes de elegibilidad (paso 1)
 * ────────────────────────────────────────────────────────────────── */

function EligibilityBadge({ status }: { status: EligibilityStatus }) {
  if (status.kind === 'eligible') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-700">
        <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
        Disponible
      </span>
    );
  }
  if (status.kind === 'waiting') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700">
        <Clock className="h-3 w-3" aria-hidden="true" />
        Faltan {status.daysRemaining} día{status.daysRemaining === 1 ? '' : 's'}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
      <ShieldOff className="h-3 w-3" aria-hidden="true" />
      Sin plan
    </span>
  );
}

function EligibilityHelp({ status, petName }: { status: EligibilityStatus; petName: string }) {
  if (status.kind === 'eligible') {
    return (
      <p className="text-xs text-emerald-700">
        Plan activo hace {status.days} día{status.days === 1 ? '' : 's'}. Listo para solicitar
        reembolsos.
      </p>
    );
  }
  if (status.kind === 'waiting') {
    return (
      <p className="text-xs text-amber-700">
        Plan activo hace {status.days} día{status.days === 1 ? '' : 's'}. Podrás pedir reembolsos a
        partir del <strong>{formatShortDate(status.availableAt)}</strong>.
      </p>
    );
  }
  return (
    <p className="text-xs text-muted-foreground">
      {petName} no tiene un plan activo.{' '}
      <a href="/contratacion" className="font-medium text-sidebar-primary underline">
        Contratá uno
      </a>{' '}
      para empezar.
    </p>
  );
}
